import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, PiggyBank, Confetti } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/currency";
import Modal from "@/components/Modal";

function GoalCard({ g, onDelete, onContribute }) {
  const pct = Math.min((g.saved_amount / g.target_amount) * 100 || 0, 100);
  const done = pct >= 100;
  const iconBg = done ? "bg-emerald-300" : "bg-blue-200";
  const barColor = done ? "bg-emerald-300" : "bg-blue-300";

  return (
    <div className="brutal-card p-5" data-testid={`goal-card-${g.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center brutal-shadow-sm ${iconBg}`}>
            {done ? <Confetti size={18} weight="bold" /> : <PiggyBank size={18} weight="bold" />}
          </div>
          <div>
            <div className="font-outfit font-black text-lg leading-tight">{g.name}</div>
            {g.deadline && <div className="text-xs text-black/50 font-manrope">By {new Date(g.deadline).toLocaleDateString()}</div>}
          </div>
        </div>
        <button onClick={() => onDelete(g.id)} className="w-8 h-8 border-2 border-black rounded-lg bg-white hover:bg-red-200 flex items-center justify-center" data-testid={`delete-goal-${g.id}`}>
          <Trash size={14} weight="bold" />
        </button>
      </div>

      <div className="flex items-end justify-between mb-2">
        <div className="font-outfit font-black text-2xl">{formatMoney(g.saved_amount, g.currency)}</div>
        <div className="text-sm font-manrope text-black/60">of {formatMoney(g.target_amount, g.currency)}</div>
      </div>

      <div className="h-4 bg-white border-2 border-black rounded-full overflow-hidden brutal-shadow-sm">
        <div className={`h-full ${barColor}`} style={{ width: `${pct}%`, transition: "width 400ms ease" }} />
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="text-xs font-bold uppercase tracking-widest">{Math.round(pct)}% saved</div>
        <button onClick={() => onContribute(g)} className="brutal-btn bg-yellow-200 hover:bg-yellow-300 px-3 py-1 text-xs" data-testid={`contribute-btn-${g.id}`}>
          + Add funds
        </button>
      </div>
    </div>
  );
}

function GoalModal({ open, onClose, onSaved }) {
  const { currency } = useAuth();
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [ccy, setCcy] = useState(currency);
  const [deadline, setDeadline] = useState("");
  const [currencies, setCurrencies] = useState([currency]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setTarget(""); setDeadline(""); setCcy(currency);
  }, [open, currency]);

  useEffect(() => {
    api.get("/currencies")
      .then(({ data }) => setCurrencies(data.map((c) => c.code)))
      .catch((err) => console.error("Failed to load currencies:", err));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/goals", {
        name, target_amount: parseFloat(target),
        currency: ccy, saved_amount: 0,
        deadline: deadline ? new Date(deadline).toISOString() : null,
      });
      toast.success("Goal created");
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New savings goal" testId="goal-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="goal-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">Goal name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="brutal-input" placeholder="Spring break trip" data-testid="goal-name-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Target</label>
            <input type="number" step="0.01" min="0" required value={target} onChange={(e) => setTarget(e.target.value)} className="brutal-input" placeholder="600" data-testid="goal-target-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Currency</label>
            <select value={ccy} onChange={(e) => setCcy(e.target.value)} className="brutal-input" data-testid="goal-currency-select">
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">Deadline (optional)</label>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="brutal-input" data-testid="goal-deadline-input" />
        </div>
        <button type="submit" disabled={saving} className="brutal-btn bg-violet-300 hover:bg-violet-400 w-full py-2.5 disabled:opacity-50" data-testid="goal-submit-btn">
          {saving ? "Saving…" : "Create goal"}
        </button>
      </form>
    </Modal>
  );
}

function ContributeModal({ goal, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (goal) setAmount(""); }, [goal]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/goals/${goal.id}/contribute`, { amount: parseFloat(amount) });
      toast.success("Added to your goal!");
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={!!goal} onClose={onClose} title={goal ? `Add to “${goal.name}”` : ""} testId="contribute-modal">
      {goal && (
        <form onSubmit={submit} className="space-y-4" data-testid="contribute-form">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Amount ({goal.currency})</label>
            <input type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)} className="brutal-input" placeholder="0.00" data-testid="contribute-amount-input" />
          </div>
          <button type="submit" disabled={saving} className="brutal-btn bg-emerald-300 hover:bg-emerald-400 w-full py-2.5 disabled:opacity-50" data-testid="contribute-submit-btn">
            {saving ? "Saving…" : "Add funds"}
          </button>
        </form>
      )}
    </Modal>
  );
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [contribute, setContribute] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/goals");
      setGoals(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      setGoals((g) => g.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tight">Savings goals</h1>
          <p className="font-manrope text-black/60 mt-1">Spring break trip? New laptop? Set a target.</p>
        </div>
        <button onClick={() => setOpen(true)} className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2.5 flex items-center gap-2" data-testid="new-goal-btn">
          <Plus size={18} weight="bold" /> New goal
        </button>
      </div>

      {loading && <div className="brutal-card p-6 font-manrope">Loading…</div>}
      {!loading && goals.length === 0 && (
        <div className="brutal-card p-12 text-center">
          <PiggyBank size={48} weight="duotone" className="mx-auto mb-2" />
          <div className="font-outfit font-black text-2xl mb-2">No goals yet</div>
          <p className="font-manrope text-black/50 mb-4">Set a savings target — even small amounts add up.</p>
          <button onClick={() => setOpen(true)} className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2">Create your first goal</button>
        </div>
      )}
      {!loading && goals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="goals-list">
          {goals.map((g) => (
            <GoalCard key={g.id} g={g} onDelete={remove} onContribute={setContribute} />
          ))}
        </div>
      )}

      <GoalModal open={open} onClose={() => setOpen(false)} onSaved={load} />
      <ContributeModal goal={contribute} onClose={() => setContribute(null)} onSaved={load} />
    </div>
  );
}
